/**
 * Deal insight engine — generates "why this is a deal" text,
 * trust/risk notes, and human-readable score labels.
 */

// ── Score Labels ──────────────────────────────────────────────
const SCORE_LABELS = [
  { min: 90, label: "Exceptional", color: "text-emerald-400" },
  { min: 75, label: "Strong",      color: "text-emerald-500" },
  { min: 60, label: "Promising",   color: "text-amber-500" },
  { min: 40, label: "Below Target", color: "text-orange-400" },
  { min: 0,  label: "Needs Review", color: "text-red-400" },
];

export function getScoreLabel(score) {
  for (const tier of SCORE_LABELS) {
    if (score >= tier.min) return tier;
  }
  return SCORE_LABELS[SCORE_LABELS.length - 1];
}

export function formatScoreWithLabel(score) {
  const tier = getScoreLabel(score);
  return { score, label: tier.label, color: tier.color };
}

// ── Outlier / Trust Note Detection ────────────────────────────
const CAP_RATE_CEILING = 20;         // Cap rates above this are suspicious
const CASH_FLOW_CEILING = 8000;      // Monthly CF above this needs verification
const RENT_TO_PRICE_CEILING = 0.03;  // Rent/price ratio above 3% per month is extreme

export function getTrustNotes(deal) {
  const notes = [];
  const p = deal.property || deal;
  const price = p.price || 0;
  const rent = deal.estimated_monthly_rent || deal.est_rent || 0;
  const capRate = deal.cap_rate || 0;
  const cashFlow = deal.cash_flow_monthly || deal.cash_flow || 0;

  if (capRate > CAP_RATE_CEILING) {
    notes.push("High projected returns — verify local comps");
  }

  if (cashFlow > CASH_FLOW_CEILING) {
    notes.push("Unusually high cash flow — confirm rent estimate");
  }

  if (price > 0 && rent > 0 && (rent / price) > RENT_TO_PRICE_CEILING) {
    notes.push("Rent estimate above typical range for price point");
  }

  if (!rent || rent <= 0) {
    notes.push("Rent estimate unavailable");
  }

  return notes;
}

// ── "Why This Is a Deal" Insight Generator ────────────────────
export function getDealInsight(deal) {
  const p = deal.property || deal;
  const price = p.price || 0;
  const rent = deal.estimated_monthly_rent || deal.est_rent || 0;
  const cashFlow = deal.cash_flow_monthly || deal.cash_flow || 0;
  const capRate = deal.cap_rate || 0;
  const dealScore = deal.deal_score || 0;

  // Skip insight if core data missing
  if (!price || !rent) return null;

  const annualCF = Math.round(cashFlow * 12);
  const rentToPrice = price > 0 ? (rent / price) : 0;

  // Priority 1: Very high monthly cash flow (but realistic)
  if (cashFlow >= 1000 && cashFlow <= CASH_FLOW_CEILING) {
    return {
      text: `$${cashFlow.toLocaleString(undefined, {maximumFractionDigits: 0})}/mo cash flow · ~$${annualCF.toLocaleString()}/yr`,
      type: "cashflow",
    };
  }

  // Priority 2: Strong rent-to-price ratio (1% rule pass)
  if (rentToPrice >= 0.01 && rentToPrice <= RENT_TO_PRICE_CEILING) {
    const pct = (rentToPrice * 100).toFixed(1);
    return {
      text: `${pct}% rent-to-price ratio · passes the 1% rule`,
      type: "ratio",
    };
  }

  // Priority 3: High cap rate (within realistic range)
  if (capRate >= 7 && capRate <= CAP_RATE_CEILING) {
    return {
      text: `${capRate.toFixed(1)}% cap rate · strong yield`,
      type: "caprate",
    };
  }

  // Priority 4: Positive cash flow with annual projection
  if (cashFlow > 0) {
    return {
      text: `Projected cash flow: $${cashFlow.toLocaleString(undefined, {maximumFractionDigits: 0})}/mo`,
      type: "cashflow",
    };
  }

  // Priority 5: Score-based fallback
  if (dealScore >= 75) {
    return {
      text: "Top-ranked opportunity in this market",
      type: "score",
    };
  }

  return null;
}

// ── Deal of the Day Title Generator ───────────────────────────
export function getDealOfDayTitle(deal) {
  const cashFlow = deal.cash_flow_monthly || deal.cash_flow || 0;
  const dealScore = deal.deal_score || 0;

  if (dealScore >= 90) return "Top Ranked Deal Today";
  if (cashFlow >= 2000) return "Highest Cash Flow Deal Today";
  if (dealScore >= 75) return "Best Deal Found Today";
  return "Featured Deal of the Day";
}

// ── Quality Filter ────────────────────────────────────────────
export function meetsQualityThreshold(deal) {
  const p = deal.property || deal;
  const price = p.price || 0;
  const rent = deal.estimated_monthly_rent || deal.est_rent || 0;
  const cashFlow = deal.cash_flow_monthly || deal.cash_flow || 0;
  const dealScore = deal.deal_score || 0;

  if (!price || price <= 0) return false;
  if (!rent || rent <= 0) return false;
  if (cashFlow <= 0) return false;
  if (dealScore < 50) return false;

  return true;
}

// Stricter filter for "featured" section (Deal of Day, top 3)
export function meetsFeaturedThreshold(deal) {
  if (!meetsQualityThreshold(deal)) return false;
  const dealScore = deal.deal_score || 0;
  if (dealScore < 70) return false;
  return true;
}
